/**
 * Multi-hop chain timeline helpers for HarnessesBrowser §18.5c.
 *
 * Awaiting-user resume chains can fan out: a parent task hits
 * `awaiting_user`, the user resumes with answers, the server spawns a
 * child task; that child can itself await + resume again. The
 * harn-service stamps each child with `parentTaskId` + `chainDepth`,
 * but to walk the whole chain the client must follow `parentTaskId`
 * up (to find the root) and `?parent=<id>` down (to find descendants).
 *
 * This module owns the pure walk + tree-flatten logic so the UI
 * component stays focused on rendering. All fetches are injected so
 * the helpers stay testable without a live HarnClient.
 */

import type { HarnTaskRecord, HarnTaskSummary } from "../types/harn";

/** Hard cap on ancestor walk to prevent runaway loops on cyclic data. */
const MAX_ANCESTOR_HOPS = 32;
/** Hard cap on descendant walk to prevent unbounded fan-out. */
const MAX_DESCENDANT_DEPTH = 8;

/**
 * Chase `parentTaskId` upward to find the root of a chain. Returns the
 * root task's id (which equals the input when the input has no parent).
 *
 * `getTask` is the injection point — pass `(id) => client.getTask(id)`
 * in production. The walk caps at `MAX_ANCESTOR_HOPS` to defend
 * against malformed cycles.
 */
export async function findChainRoot(
  startId: string,
  getTask: (id: string) => Promise<Pick<HarnTaskRecord, "taskId" | "parentTaskId">>,
): Promise<string> {
  let currentId = startId;
  for (let i = 0; i < MAX_ANCESTOR_HOPS; i++) {
    const task = await getTask(currentId);
    if (!task.parentTaskId) return task.taskId;
    currentId = task.parentTaskId;
  }
  // Cap reached — return the deepest ancestor we found, even if a parent still exists.
  return currentId;
}

/** One node in the flattened chain timeline. */
export interface ChainNode {
  /** Depth from the root (root = 0, root's child = 1, etc.). */
  depth: number;
  task: HarnTaskSummary;
}

/**
 * Walk descendants breadth-first from `rootId` and return a flat list
 * sorted in chain order (root first, then children, then grandchildren,
 * each level sorted by `createdAt` ascending).
 *
 * `getRoot` returns the root task summary (so we don't need a separate
 * fetch path for it). `listChildren(parentId)` returns the direct
 * children of that parent — wrap `client.listTasks({ parent })` in
 * production.
 *
 * Walks cap at `MAX_DESCENDANT_DEPTH`; deeper subtrees emit a
 * truncated marker via the returned `truncated` flag.
 */
export async function walkChainDescendants(
  rootId: string,
  getRoot: (id: string) => Promise<HarnTaskSummary>,
  listChildren: (parentId: string) => Promise<HarnTaskSummary[]>,
): Promise<{ nodes: ChainNode[]; truncated: boolean }> {
  const root = await getRoot(rootId);
  const nodes: ChainNode[] = [{ depth: 0, task: root }];
  let truncated = false;

  // BFS frontier of (parentId, depth) pairs.
  let frontier: Array<{ parentId: string; depth: number }> = [{ parentId: rootId, depth: 0 }];

  while (frontier.length > 0) {
    const next: Array<{ parentId: string; depth: number }> = [];
    for (const { parentId, depth } of frontier) {
      if (depth + 1 > MAX_DESCENDANT_DEPTH) {
        truncated = true;
        continue;
      }
      const children = await listChildren(parentId);
      // Stable order: createdAt ascending so the timeline reads top-down chronologically.
      const sorted = [...children].sort((a, b) => a.createdAt - b.createdAt);
      for (const child of sorted) {
        nodes.push({ depth: depth + 1, task: child });
        next.push({ parentId: child.taskId, depth: depth + 1 });
      }
    }
    frontier = next;
  }

  return { nodes, truncated };
}
